import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { $convertToMarkdownString } from '@lexical/markdown';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

import {
  useAddVoiceNoteMutation,
  useFollowUpTemplateQuery,
  useSaveFollowUpTemplateMutation,
} from '../../../../generated/graphql';

import AudioRecorderPlayer, {
  AudioRecorderPlayerRef,
} from '../../../components/AudioRecorderPlayer';
import Button from '../../../components/Button';
import Editor from '../../../components/Editor';
import '../../../components/Editor/index.css';
import { MARKDOWN_TRANSFORMERS } from '../../../components/Editor/plugins/MarkdownTransformers';
import InputGroup from '../../../components/InputGroup';
import InputLabel from '../../../components/InputLabel';
import { customToast } from '../../../components/ToastAlert/customToast';
import { FollowUpForm } from '../../../lib/followUp';
import { activitiesFingerprint } from '../../../lib/mailSequence';
import { formatProviderActivityForFollowUp } from '../../../lib/providerActivity';
import { isAbortError } from '../../../../lib/errors';
import usePaintScreen from '../../../hooks/usePaintScreen';
import Spinner from '../../../svgs/Spinner';
import {
  MAX_SUBJECT_LENGTH,
  savedActivityInputPreviewToActivityTemplateInput,
} from '../../FollowUpBuilder/helpers';
import ScheduleMailResourcesSection from '../../ScheduleMail/ScheduleMailResourcesSection';
import TemplateEditorShell from './TemplateEditorShell';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

const LIST_PATH = '/automations/templates/mail';

type BaselineSnapshot = {
  name: string;
  subject: string;
  note: string;
  providerVoiceNoteId: string | null;
  activitiesFp: string;
};

const MailTemplateEditor: FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId?: string }>();
  const isCreate = !templateId;
  const paintScreen = usePaintScreen();

  const audioRecorderPlayerRef = useRef<AudioRecorderPlayerRef | null>(null);
  const abortAddVoiceNote = useRef(new AbortController());
  const [editorKey, setEditorKey] = useState(0);
  const [draftedVoiceNoteMediaUrl, setDraftedVoiceNoteMediaUrl] = useState<
    string | undefined
  >();
  const [name, setName] = useState('');
  const [baseline, setBaseline] = useState<BaselineSnapshot | null>(null);
  const [hydrated, setHydrated] = useState(isCreate);

  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<FollowUpForm>({
    defaultValues: {
      subject: '',
      note: '',
      providerVoiceNoteId: null,
      activities: [],
      patientsOrTags: [],
    },
  });

  const { remove, prepend, update } = useFieldArray({
    control,
    name: 'activities',
  });

  const watchSubject = watch('subject');
  const watchNote = watch('note');
  const watchProviderVoiceNoteId = watch('providerVoiceNoteId');
  const activities = watch('activities') ?? [];
  const hasRecordedVoiceNote = Boolean(watchProviderVoiceNoteId);

  const { data, loading: loadingTemplate } = useFollowUpTemplateQuery({
    variables: { followUpTemplateId: templateId ?? '' },
    skip: isCreate || !templateId,
    fetchPolicy: 'network-only',
    onError: () => {
      customToast.error('Unable to load Mail template.');
      navigate(LIST_PATH, { replace: true });
    },
  });

  const hasUnsavedChanges = useMemo(() => {
    if (!baseline) return false;
    return (
      name.trim() !== baseline.name ||
      (watchSubject ?? '') !== baseline.subject ||
      (watchNote ?? '') !== baseline.note ||
      (watchProviderVoiceNoteId ?? null) !== baseline.providerVoiceNoteId ||
      activitiesFingerprint(activities) !== baseline.activitiesFp
    );
  }, [
    activities,
    baseline,
    name,
    watchNote,
    watchProviderVoiceNoteId,
    watchSubject,
  ]);

  const [addVoiceNoteMutation, { loading: addVoiceNoteLoading }] =
    useAddVoiceNoteMutation();

  const [saveTemplate, { loading: saving }] = useSaveFollowUpTemplateMutation({
    onCompleted: ({ saveFollowUpTemplate }) => {
      if (saveFollowUpTemplate) {
        customToast.success(
          isCreate ? 'Mail template created.' : 'Mail template updated.',
        );
        setBaseline(null);
        closeUnsavedChangesModal();
        navigate(LIST_PATH);
      }
    },
    onError: (err) => {
      customToast.error(err.message || 'Could not save Mail template.');
    },
  });

  useEffect(() => {
    if (isCreate) {
      setBaseline({
        name: '',
        subject: '',
        note: '',
        providerVoiceNoteId: null,
        activitiesFp: '',
      });
      setHydrated(true);
      return;
    }

    const template = data?.followUpTemplate;
    if (!template) return;

    const formattedActivities = template.activityTemplates.map(
      (activityTemplate) =>
        formatProviderActivityForFollowUp(
          activityTemplate.activity,
          1,
          activityTemplate.providerNote,
        ),
    );
    const nextName = template.name?.trim() ?? '';
    const nextSubject = template.subject ?? '';
    const nextNote = template.providerNote ?? '';
    const nextVoiceNoteId = template.providerVoiceNote?.id ?? null;

    setName(nextName);
    reset({
      subject: nextSubject,
      note: nextNote,
      providerVoiceNoteId: nextVoiceNoteId,
      activities: formattedActivities,
      patientsOrTags: [],
    });
    setBaseline({
      name: nextName,
      subject: nextSubject,
      note: nextNote,
      providerVoiceNoteId: nextVoiceNoteId,
      activitiesFp: activitiesFingerprint(formattedActivities),
    });
    setDraftedVoiceNoteMediaUrl(template.providerVoiceNote?.media.url);
    setEditorKey((k) => k + 1);
    setHydrated(true);
    void audioRecorderPlayerRef.current?.resetRecorder();
  }, [data?.followUpTemplate, isCreate, reset]);

  useEffect(() => {
    return () => {
      abortAddVoiceNote.current.abort();
    };
  }, []);

  const {
    isUnsavedChangesModalOpen,
    closeUnsavedChangesModal,
    goBack,
    confirmDiscard,
  } = useUnsavedChangesGuard({
    hasUnsavedChanges,
    blockNavigation: Boolean(hasUnsavedChanges && !saving),
    exitPath: LIST_PATH,
    onDiscard: () => setBaseline(null),
  });

  const onAddVoiceNote = async (audioFile: File, audioDuration: number) => {
    try {
      abortAddVoiceNote.current = new AbortController();
      const { data: voiceData } = await addVoiceNoteMutation({
        variables: {
          input: {
            file: audioFile,
            duration: audioDuration,
          },
        },
        context: { fetchOptions: { signal: abortAddVoiceNote.current.signal } },
      });
      setValue('providerVoiceNoteId', voiceData?.addVoiceNote.id ?? null, {
        shouldDirty: true,
      });
    } catch (err) {
      if (isAbortError(err as ApolloError)) return;
      customToast.error('Something went wrong.');
    }
  };

  const onDiscardVoiceNote = () => {
    abortAddVoiceNote.current.abort();
    setValue('providerVoiceNoteId', null, { shouldDirty: true });
    setDraftedVoiceNoteMediaUrl(undefined);
  };

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      customToast.error('Template name is required.');
      return;
    }
    const {
      subject,
      note,
      providerVoiceNoteId,
      activities: acts,
    } = getValues();
    if (!subject.trim()) {
      customToast.error('Subject is required.');
      return;
    }

    saveTemplate({
      variables: {
        input: {
          id: templateId ?? undefined,
          name: trimmedName,
          subject: subject.trim(),
          isDraft: false,
          providerNote: note ?? '',
          providerVoiceNoteId: providerVoiceNoteId ?? null,
          activityTemplates: (acts ?? []).map(
            savedActivityInputPreviewToActivityTemplateInput,
          ),
        },
      },
    });
  };

  const showPage = paintScreen && hydrated && (isCreate || !loadingTemplate);
  const title =
    name.trim() || (isCreate ? 'New Mail template' : 'Mail template');

  return (
    <TemplateEditorShell
      show={showPage}
      title={title}
      onBack={goBack}
      headerActions={
        <>
          {saving && <Spinner className="h-5 w-5 text-neutral-125" />}
          <Button
            title={saving ? 'Saving...' : 'Save'}
            size="small"
            className="px-6 py-1.5"
            onClick={handleSave}
            disabled={saving || !name.trim() || !watchSubject?.trim()}
          />
        </>
      }
      isUnsavedChangesModalOpen={isUnsavedChangesModalOpen}
      onCloseUnsavedChangesModal={closeUnsavedChangesModal}
      onConfirmDiscard={confirmDiscard}
    >
      <div className="w-full max-w-3xl space-y-6 px-6">
        <p className="text-caption text-neutral-110">
          Edit the name, subject, message, voice note, and resources for this
          Mail template. Changes apply the next time it is used.
        </p>

        <InputGroup
          label="Template name"
          placeholder="e.g. Post-session check-in"
          useNaturalLettering
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={50}
        />

        <InputGroup
          label="Subject"
          placeholder="e.g. Checking in after our session"
          useNaturalLettering
          value={watchSubject ?? ''}
          onChange={(e) =>
            setValue('subject', e.target.value, { shouldDirty: true })
          }
          maxLength={MAX_SUBJECT_LENGTH}
        />

        <div>
          <div
            className={classNames(
              'mb-2 flex',
              !hasRecordedVoiceNote && 'flex-row items-center justify-between',
              hasRecordedVoiceNote && 'flex-col items-start justify-start',
            )}
          >
            <InputLabel label="Message" useNaturalLettering />
            <AudioRecorderPlayer
              recordingEnabled
              isUploading={addVoiceNoteLoading}
              audioMediaUrl={draftedVoiceNoteMediaUrl}
              hasUploaded={hasRecordedVoiceNote}
              onAdd={onAddVoiceNote}
              onDiscard={onDiscardVoiceNote}
              filePrefix="voicenote"
              controlsClassName="!flex-row-reverse h-[36px]"
              ref={audioRecorderPlayerRef}
            />
          </div>
          <Editor
            elKey={`mail-template-editor-${templateId ?? 'new'}-${editorKey}`}
            placeholder="Hi there, to follow up on our session..."
            className="text-neutral-150"
            containerClassName="w-full"
            initialContentMarkdown={watchNote ?? ''}
            onChange={(editorState) => {
              editorState.read(() => {
                setValue(
                  'note',
                  $convertToMarkdownString(
                    MARKDOWN_TRANSFORMERS,
                    undefined,
                    true,
                  ),
                  { shouldDirty: true },
                );
              });
            }}
            onError={(error: Error) => {
              console.error(error);
            }}
          />
        </div>

        <div>
          <InputLabel
            label="Resources"
            useNaturalLettering
            containerClassName="mb-2"
          />
          <ScheduleMailResourcesSection
            activities={activities}
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            remove={remove}
            update={update}
            prepend={prepend}
            compactLayout
            hideSectionLabel
          />
        </div>
      </div>
    </TemplateEditorShell>
  );
};

export default MailTemplateEditor;
